/**
 * v1 (o que esta no banco) -> v2 (total, sem opcionais).
 *
 * Duas regras que valem para o arquivo inteiro:
 *
 * 1. NUNCA FALHA. Nao ha caminho de excecao. Entrada podre vira default mais
 *    um diagnostico. Um `opacity` invalido nao pode derrubar a config toda —
 *    perder 13 elementos por causa de 1 campo seria pior que o bug original.
 *
 * 2. NADA DE SPREAD. Todo campo e lido, validado e atribuido explicitamente.
 *    O spread raso (`{ ...DEFAULTS, ...doProdutor }`) e exatamente o mecanismo
 *    que hoje deixa `fields` metade default e metade do produtor: basta o
 *    produtor gravar um objeto `fields` parcial para os campos ausentes
 *    sumirem em vez de cair no default. Escrever campo a campo e mais verboso
 *    e e o ponto.
 */

import type { ElementId, Diagnostic, DiagnosticCode, DiagnosticSeverity } from '../types.js';
import { ELEMENT_ORDER } from '../types.js';
import {
  type V1Config, type V1Element, type V1Fields, type V1Style,
  type V1TextConfig, type V1TextPositions, type V1FontSizes, type V1Signer,
  V1_DEFAULT_TEXT, V1_DEFAULT_TEXT_POSITIONS, V1_DEFAULT_FONT_SIZES,
  WEB_CANVAS_WIDTH, v1MigrateLegacyPositions, v1DefaultElements,
} from './v1.js';
import {
  type V2Config, type V2Element, type V2Style, type V2Fields, type V2Text,
  type V2Signer, type FontSizeToken, type FontWeight, type TextAlign,
  type EventDateMode,
  FONT_SIZE_PX, V2_DEFAULT_FIELDS, V2_DEFAULT_STYLE, V2_DEFAULT_TEXT_COLOR,
} from './v2.js';

// ─── Coletor de diagnosticos ────────────────────────────────────────────────

class Diagnostics {
  private readonly items: Diagnostic[] = [];

  add(
    code: DiagnosticCode,
    severity: DiagnosticSeverity,
    message: string,
    element?: ElementId,
  ): void {
    this.items.push(element ? { code, severity, message, element } : { code, severity, message });
  }

  /**
   * Ordena para a saida ser estavel: o mesmo config sempre produz a mesma
   * lista, na mesma ordem. Diagnostico entra em golden test, e uma ordem que
   * depende de iteracao de objeto faria o teste piscar.
   */
  freeze(): readonly Diagnostic[] {
    const rank: Record<DiagnosticSeverity, number> = { error: 0, warn: 1, info: 2 };
    return [...this.items].sort(
      (a, b) =>
        rank[a.severity] - rank[b.severity] ||
        a.code.localeCompare(b.code) ||
        (a.element ?? '').localeCompare(b.element ?? '') ||
        a.message.localeCompare(b.message),
    );
  }
}

// ─── Coercao primitiva ──────────────────────────────────────────────────────
//
// Faz o papel que um validador de schema faria, com uma diferenca que importa
// aqui: cada funcao recupera o VALOR, nunca rejeita o objeto. E a diferenca
// entre "este campo caiu no default" e "esta config inteira e invalida".

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const bool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback;

/** Numero finito. Rejeita NaN e Infinity, que sobrevivem a `typeof === number`. */
const finiteNumber = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const str = (v: unknown): string | null =>
  typeof v === 'string' ? v : null;

/**
 * Texto de exibicao: NFC, sem controle, sem espaco nas pontas.
 *
 * NFC porque "ç" pode chegar como um code point ou como c + cedilha combinante.
 * Visualmente identicos, larguras diferentes — e a medida do layout mudaria
 * conforme o teclado de quem digitou.
 *
 * Quebra de linha vira espaco: o wrap e decidido pela largura da caixa, no
 * layout. Configs reais tem `conclusionText` terminando em "\n\n\n", que hoje
 * viraria linha vazia no fim do bloco.
 */
const displayText = (v: unknown): string | null => {
  const s = str(v);
  if (s === null) return null;
  return s.normalize('NFC').replace(/[\r\n\t]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
};

/** Hex `#rgb` ou `#rrggbb` -> `#rrggbb` minusculo. */
const hexColor = (v: unknown): string | null => {
  const s = str(v)?.trim();
  if (!s) return null;
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(s);
  if (!m) return null;
  const h = m[1].toLowerCase();
  return h.length === 3 ? `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}` : `#${h}`;
};

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/**
 * Quantiza a 6 casas.
 *
 * As posicoes vem de arrastar o mouse e chegam com 14 digitos
 * (52.01468085265911). Sem quantizar, um bit de diferenca no float vira hash
 * diferente e o golden test pisca sem que nada de real tenha mudado.
 */
const q6 = (n: number): number => Math.round(n * 1e6) / 1e6;

const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | null => {
  const s = str(v);
  return s !== null && (allowed as readonly string[]).includes(s) ? (s as T) : null;
};

const FONT_SIZES = Object.keys(FONT_SIZE_PX) as FontSizeToken[];
const FONT_WEIGHTS: readonly FontWeight[] = ['normal', 'semibold', 'bold'];
const TEXT_ALIGNS: readonly TextAlign[] = ['left', 'center', 'right'];
const DATE_MODES: readonly EventDateMode[] = ['start', 'end', 'range'];

/** "0.2em" | "0.2" | 0.2 -> 0.2 */
const emValue = (v: unknown): number | null => {
  const n = finiteNumber(v);
  if (n !== null) return n;
  const s = str(v)?.trim();
  if (!s) return null;
  const parsed = Number.parseFloat(s.endsWith('em') ? s.slice(0, -2) : s);
  return Number.isFinite(parsed) ? parsed : null;
};

// ─── Normalizacao ───────────────────────────────────────────────────────────

export interface NormalizeResult {
  readonly config: V2Config;
  readonly diagnostics: readonly Diagnostic[];
}

export function normalizeConfig(raw: unknown): NormalizeResult {
  const d = new Diagnostics();

  if (!isRecord(raw)) {
    d.add('LEGACY_MIGRATED', 'warn',
      'Configuração ausente ou inválida; usando o modelo padrão do certificado.');
    return { config: buildConfig({}, d), diagnostics: d.freeze() };
  }

  return { config: buildConfig(raw as V1Config, d), diagnostics: d.freeze() };
}

function buildConfig(v1: V1Config, d: Diagnostics): V2Config {
  const fields = normalizeFields(v1, d);
  const text = normalizeText(v1.textConfig, d);
  const textColor = normalizeTextColor(v1.textColor, d);
  const elements = normalizeElements(v1, fields, text, d);

  return {
    version: 2,
    textColor,
    backgroundUrl: str(v1.backgroundUrl)?.trim() || null,
    elements,
    fields,
    text,
    signers: normalizeSigners(v1.signers, d),
    workloadHours: normalizeWorkload(v1.workloadHours, d),
    eligibleTierIds: Array.isArray(v1.eligible_tier_ids)
      ? v1.eligible_tier_ids.filter((x): x is string => typeof x === 'string')
      : [],
  };
}

function normalizeTextColor(raw: unknown, d: Diagnostics): string {
  if (raw === undefined || raw === null) return V2_DEFAULT_TEXT_COLOR;
  const hex = hexColor(raw);
  if (hex !== null) return hex;
  d.add('VALUE_CLAMPED', 'warn',
    `Cor global "${String(raw)}" não é um hex válido; usando ${V2_DEFAULT_TEXT_COLOR}.`);
  return V2_DEFAULT_TEXT_COLOR;
}

/**
 * Resolve `fields`, com a precedencia entre os dois lugares que dizem a mesma
 * coisa.
 *
 * REGRA: `fields.X` ganha da raiz `X`.
 *
 * Motivo: `fields` e o que o editor escreve hoje; a raiz e resquicio de uma
 * versao anterior que ninguem atualiza mais. Manter a raiz vencendo faria uma
 * edicao no editor nao ter efeito — o pior resultado possivel.
 *
 * O conflito e real: 3 configs de producao tem `fields.showEventLocation`
 * false e raiz true. Esses passam a esconder o local, que e o que o produtor
 * pediu por ultimo. Cada divergencia emite diagnostico, para a mudanca ficar
 * visivel em vez de silenciosa.
 */
function normalizeFields(v1: V1Config, d: Diagnostics): V2Fields {
  const f: V1Fields = isRecord(v1.fields) ? (v1.fields as V1Fields) : {};

  const pick = (
    key: keyof V2Fields,
    fromFields: unknown,
    fromRoot: unknown,
    fallback: boolean,
  ): boolean => {
    const inFields = typeof fromFields === 'boolean';
    const inRoot = typeof fromRoot === 'boolean';
    if (inFields && inRoot && fromFields !== fromRoot) {
      d.add('FIELD_CONFLICT', 'info',
        `"${key}": o editor diz ${fromFields}, a chave antiga na raiz diz ${fromRoot}. ` +
        'Vale o do editor.');
    }
    if (inFields) return fromFields as boolean;
    if (inRoot) return fromRoot as boolean;
    return fallback;
  };

  const dateMode =
    oneOf(f.eventDateMode, DATE_MODES) ?? V2_DEFAULT_FIELDS.eventDateMode;
  if (f.eventDateMode !== undefined && oneOf(f.eventDateMode, DATE_MODES) === null) {
    d.add('VALUE_CLAMPED', 'warn',
      `Modo de data "${String(f.eventDateMode)}" desconhecido; usando "${dateMode}".`);
  }

  // showCPF/maskCPF colapsam em showDocument: o mascaramento nao e mais
  // opcional, entao maskCPF deixa de ter efeito. Quem tinha showCPF com
  // maskCPF=false passa a ver o documento mascarado.
  const showDocument = bool(f.showCPF, V2_DEFAULT_FIELDS.showDocument);
  if (showDocument && f.maskCPF === false) {
    d.add('DOCUMENT_MASKED', 'warn',
      'O documento do participante passa a sair sempre mascarado, mesmo com ' +
      '"CPF completo" marcado: o certificado é público por código.');
  }

  return {
    showParticipantName: bool(f.showParticipantName, V2_DEFAULT_FIELDS.showParticipantName),
    showParticipantLastName: bool(f.showParticipantLastName, V2_DEFAULT_FIELDS.showParticipantLastName),
    showDocument,
    showEventName: bool(f.showEventName, V2_DEFAULT_FIELDS.showEventName),
    showEventDate: pick('showEventDate', f.showEventDate, v1.showEventDate, V2_DEFAULT_FIELDS.showEventDate),
    showEventTime: pick('showEventTime', f.showEventTime, v1.showEventTime, V2_DEFAULT_FIELDS.showEventTime),
    showEventLocation: pick('showEventLocation', f.showEventLocation, v1.showEventLocation, V2_DEFAULT_FIELDS.showEventLocation),
    showWorkload: pick('showWorkload', f.showWorkload, v1.showWorkload, V2_DEFAULT_FIELDS.showWorkload),
    showSigners: bool(f.showSigners, V2_DEFAULT_FIELDS.showSigners),
    showQRCode: bool(f.showQRCode, V2_DEFAULT_FIELDS.showQRCode),
    eventDateMode: dateMode,
  };
}

function normalizeText(raw: unknown, d: Diagnostics): V2Text {
  const t: V1TextConfig = isRecord(raw) ? (raw as V1TextConfig) : {};

  const pick = (key: keyof V2Text, fallback: string): string => {
    if (t[key] === undefined || t[key] === null) return fallback;
    const clean = displayText(t[key]);
    if (clean === null) {
      d.add('VALUE_CLAMPED', 'warn', `Texto "${key}" não é texto; usando o padrão.`);
      return fallback;
    }
    return clean;
  };

  return {
    title: pick('title', V1_DEFAULT_TEXT.title),
    introText: pick('introText', V1_DEFAULT_TEXT.introText),
    participationText: pick('participationText', V1_DEFAULT_TEXT.participationText),
    // Sem default: vazio significa "o produtor nao quer este bloco".
    conclusionText: pick('conclusionText', ''),
  };
}

function normalizeWorkload(raw: unknown, d: Diagnostics): number | null {
  if (raw === undefined || raw === null) return null;
  const n = finiteNumber(raw);
  if (n === null) {
    d.add('VALUE_CLAMPED', 'warn', `Carga horária "${String(raw)}" não é número; ignorada.`);
    return null;
  }
  if (n < 0) {
    d.add('VALUE_CLAMPED', 'warn', `Carga horária negativa (${n}); ignorada.`);
    return null;
  }
  // 0 significa "nao informado", que e como os 15 configs de producao usam.
  return n === 0 ? null : q6(n);
}

function normalizeSigners(raw: unknown, d: Diagnostics): readonly V2Signer[] {
  if (!Array.isArray(raw)) return [];

  const out: V2Signer[] = [];
  for (const item of raw as V1Signer[]) {
    if (!isRecord(item)) continue;
    const name = displayText(item.name) ?? '';
    const role = displayText(item.role) ?? '';
    // Signatario sem nome nao tem o que desenhar; a linha e o cargo sozinhos
    // pareceriam um campo em branco no certificado.
    if (!name) {
      d.add('ELEMENT_EMPTY', 'warn', 'Assinatura sem nome foi ignorada.', 'signatures');
      continue;
    }
    out.push({ name, role, signatureUrl: str(item.signatureUrl)?.trim() || null });
  }

  const MAX = 6;
  if (out.length > MAX) {
    d.add('SIGNERS_OVERFLOW', 'warn',
      `${out.length} assinaturas configuradas; o certificado comporta ${MAX}.`,
      'signatures');
    return out.slice(0, MAX);
  }
  return out;
}

// ─── Elementos ──────────────────────────────────────────────────────────────

function normalizeElements(
  v1: V1Config,
  fields: V2Fields,
  text: V2Text,
  d: Diagnostics,
): Readonly<Record<ElementId, V2Element>> {
  const source = resolveElementSource(v1, text, d);

  const byId = new Map<string, V1Element>();
  for (const el of source) {
    const id = str(el?.id);
    if (id === null) continue;
    if (!(ELEMENT_ORDER as readonly string[]).includes(id)) {
      d.add('UNKNOWN_ELEMENT', 'info',
        `Elemento "${id}" não é reconhecido e foi descartado.`);
      continue;
    }
    // Duplicata: o ultimo vence, como um Object.fromEntries faria.
    byId.set(id, el);
  }

  const fontSizes = resolveFontSizes(v1.fontSizes);
  const out = {} as Record<ElementId, V2Element>;

  for (const id of ELEMENT_ORDER) {
    out[id] = normalizeElement(id, byId.get(id), fontSizes, d);
  }

  return out;
}

/**
 * De onde vem a lista de elementos.
 *
 * Se `elements` existe e tem itens, e a fonte da verdade — ponto. Se esta
 * ausente ou vazia (9 dos 16 configs de producao), reconstroi pelo caminho
 * legado, com a mesma aritmetica do editor web, para a aparencia nao mudar.
 */
function resolveElementSource(v1: V1Config, text: V2Text, d: Diagnostics): V1Element[] {
  if (Array.isArray(v1.elements) && v1.elements.length > 0) {
    return v1.elements.filter(isRecord) as V1Element[];
  }

  d.add('LEGACY_MIGRATED', 'info',
    'Certificado sem posicionamento por elemento; posições reconstruídas a ' +
    'partir do formato antigo.');

  const tp = resolveTextPositions(v1.textPositions, d);
  const fields: V1Fields = isRecord(v1.fields) ? (v1.fields as V1Fields) : {};

  return v1MigrateLegacyPositions(
    tp,
    {
      title: text.title,
      introText: text.introText,
      participationText: text.participationText,
      conclusionText: text.conclusionText,
    },
    resolveFontSizes(v1.fontSizes),
    fields,
  );
}

function resolveTextPositions(raw: unknown, d: Diagnostics): Required<V1TextPositions> {
  const p: V1TextPositions = isRecord(raw) ? (raw as V1TextPositions) : {};
  const one = (v: unknown, fb: number, key: string): number => {
    const n = finiteNumber(v);
    if (n === null) return fb;
    if (n < 0 || n > 100) {
      d.add('VALUE_CLAMPED', 'info', `Posição antiga "${key}" fora de 0-100 (${n}); limitada.`);
      return Math.max(0, Math.min(100, n));
    }
    return n;
  };
  return {
    titleTop: one(p.titleTop, V1_DEFAULT_TEXT_POSITIONS.titleTop, 'titleTop'),
    nameTop: one(p.nameTop, V1_DEFAULT_TEXT_POSITIONS.nameTop, 'nameTop'),
    metadataTop: one(p.metadataTop, V1_DEFAULT_TEXT_POSITIONS.metadataTop, 'metadataTop'),
  };
}

function resolveFontSizes(raw: unknown): Required<V1FontSizes> {
  const f: V1FontSizes = isRecord(raw) ? (raw as V1FontSizes) : {};
  return {
    title: oneOf(f.title, FONT_SIZES) ?? V1_DEFAULT_FONT_SIZES.title,
    name: oneOf(f.name, FONT_SIZES) ?? V1_DEFAULT_FONT_SIZES.name,
    event: oneOf(f.event, FONT_SIZES) ?? V1_DEFAULT_FONT_SIZES.event,
  };
}

function normalizeElement(
  id: ElementId,
  raw: V1Element | undefined,
  fontSizes: Required<V1FontSizes>,
  d: Diagnostics,
): V2Element {
  const preset = PRESET_BY_ID.get(id)!;

  if (raw === undefined) {
    // Elemento ausente do config: entra desligado, no lugar do preset. Ligar
    // por conta propria faria aparecer no certificado algo que o produtor
    // nunca viu no editor.
    return {
      id,
      enabled: false,
      x: q6(preset.position.x / 100),
      y: q6(preset.position.y / 100),
      width: null,
      style: normalizeStyle(id, preset.style, fontSizes, d),
    };
  }

  const pos = isRecord(raw.position) ? (raw.position as { x?: unknown; y?: unknown }) : {};

  const coord = (v: unknown, fallbackPct: number, axis: 'x' | 'y'): number => {
    const n = finiteNumber(v);
    if (n === null) return clamp01(fallbackPct / 100);
    const frac = n / 100;
    if (frac < 0 || frac > 1) {
      d.add('VALUE_CLAMPED', 'warn',
        `Posição ${axis} fora da página (${n.toFixed(1)}%); trazida para a borda.`, id);
      return clamp01(frac);
    }
    return frac;
  };

  return {
    id,
    enabled: bool(raw.enabled, true),
    x: q6(coord(pos.x, preset.position.x, 'x')),
    y: q6(coord(pos.y, preset.position.y, 'y')),
    width: normalizeWidth(raw.width, id, d),
    style: normalizeStyle(id, raw.style, fontSizes, d),
  };
}

/** Pixels do canvas 1123 -> fracao da largura. Ausente = ajusta ao conteudo. */
function normalizeWidth(raw: unknown, id: ElementId, d: Diagnostics): number | null {
  if (raw === undefined || raw === null) return null;
  const n = finiteNumber(raw);
  if (n === null || n <= 0) {
    d.add('VALUE_CLAMPED', 'info',
      `Largura inválida em "${id}"; passa a ajustar ao conteúdo.`, id);
    return null;
  }
  const frac = n / WEB_CANVAS_WIDTH;
  if (frac > 1) {
    d.add('VALUE_CLAMPED', 'warn',
      `Largura de ${n}px passa da página; limitada à largura total.`, id);
    return 1;
  }
  return q6(frac);
}

function normalizeStyle(
  id: ElementId,
  raw: V1Style | undefined,
  fontSizes: Required<V1FontSizes>,
  d: Diagnostics,
): V2Style {
  const s: V1Style = isRecord(raw) ? (raw as V1Style) : {};
  const preset = PRESET_BY_ID.get(id)!.style;

  // Cor: invalida vira null (herda a global) em vez de derrubar o elemento.
  let color: string | null = null;
  if (s.color !== undefined && s.color !== null) {
    color = hexColor(s.color);
    if (color === null) {
      d.add('VALUE_CLAMPED', 'warn',
        `Cor "${String(s.color)}" inválida; o elemento herda a cor geral.`, id);
    }
  }

  // Opacidade: aceita 0..1 e tambem 0..100, que aparece em config escrito a mao.
  let opacity = V2_DEFAULT_STYLE.opacity;
  const rawOpacity = finiteNumber(s.opacity) ?? finiteNumber(preset.opacity);
  if (rawOpacity !== null) {
    if (rawOpacity > 1 && rawOpacity <= 100) {
      opacity = clamp01(rawOpacity / 100);
      d.add('VALUE_CLAMPED', 'info',
        `Opacidade ${rawOpacity} interpretada como ${opacity}.`, id);
    } else if (rawOpacity < 0 || rawOpacity > 1) {
      opacity = clamp01(rawOpacity);
      d.add('VALUE_CLAMPED', 'warn',
        `Opacidade ${rawOpacity} fora de 0-1; limitada a ${opacity}.`, id);
    } else {
      opacity = rawOpacity;
    }
  }

  // `fontSizes` legado so vale onde o editor o aplicava, e perde para o estilo
  // do elemento. Nos configs reais os dois coexistem e concordam; a precedencia
  // importa quando o produtor muda o tamanho pelo editor novo.
  const legacySize =
    id === 'title' ? fontSizes.title
      : id === 'participantName' ? fontSizes.name
        : id === 'eventName' ? fontSizes.event
          : null;

  const fontSize =
    oneOf(s.fontSize, FONT_SIZES)
    ?? (legacySize as FontSizeToken | null)
    ?? oneOf(preset.fontSize, FONT_SIZES)
    ?? V2_DEFAULT_STYLE.fontSize;

  const letterSpacingRaw = s.letterSpacing !== undefined
    ? emValue(s.letterSpacing)
    : emValue(preset.letterSpacing);

  return {
    color,
    opacity: q6(opacity),
    fontSize,
    fontWeight: oneOf(s.fontWeight, FONT_WEIGHTS)
      ?? oneOf(preset.fontWeight, FONT_WEIGHTS)
      ?? V2_DEFAULT_STYLE.fontWeight,
    textAlign: oneOf(s.textAlign, TEXT_ALIGNS)
      ?? oneOf(preset.textAlign, TEXT_ALIGNS)
      ?? V2_DEFAULT_STYLE.textAlign,
    uppercase: bool(s.uppercase, bool(preset.uppercase, V2_DEFAULT_STYLE.uppercase)),
    letterSpacing: q6(letterSpacingRaw ?? V2_DEFAULT_STYLE.letterSpacing),
  };
}

/**
 * Preset de fabrica indexado por id, construido uma vez.
 *
 * Serve de ultima linha de defesa: quando o config nao diz nada sobre um
 * campo, o valor vem daqui — os mesmos numeros que o editor mostra.
 */
const PRESET_BY_ID: ReadonlyMap<ElementId, Required<V1Element>> = new Map(
  v1DefaultElements().map((el) => [el.id as ElementId, el] as const),
);
